import axios, { AxiosResponse, AxiosError } from 'axios'

const baseURL = import.meta.env.VITE_PROCUREMENT_API_URL

const procurementApiClient = axios.create({
  baseURL: baseURL
})

procurementApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 404) {
      window.location.replace('/not-found')
    }
    return Promise.reject(error)
  }
)

export const getRfpByToken = (token: string): Promise<RequestForProposalResponseType> =>
  procurementApiClient.get(`/vendor-ui/request-for-proposals/${token}`).then((res) => res.data)

export const putPropose = (token: string, data: ProposeRequestType): Promise<void> =>
  procurementApiClient.put(`/vendor-ui/request-for-proposals/proposals/${token}`, data).then((res) => res.data)

export const postRfpOpened = (rfpId: string, recipientId: string): Promise<void> =>
  procurementApiClient.post(`/vendor-ui/request-for-proposals/${rfpId}/opened`, { recipientId })

export const getProposalToken = (token: string, proposalId: string): Promise<string> =>
  procurementApiClient
    .get(`/vendor-ui/request-for-proposals/proposals/${proposalId}/token?uniqueRecipientToken=${token}`)
    .then((res) => res.data)

export const getProposalState = (token: string): Promise<GetProposalStateResponseType> =>
  procurementApiClient.get(`/vendor-ui/proposal/state?uniqueRecipientToken=${token}`).then((res) => res.data)

export const willPropose = (rfpId: string, recipientId: string): Promise<void> =>
  procurementApiClient.post(`/vendor-ui/request-for-proposals/${rfpId}/recipients/${recipientId}/will-submit-proposal`)

export const mayPropose = (rfpId: string, recipientId: string): Promise<void> =>
  procurementApiClient.post(`/vendor-ui/request-for-proposals/${rfpId}/recipients/${recipientId}/may-submit-proposal`)

export const willNotPropose = (rfpId: string, recipientId: string, reason: string): Promise<void> =>
  procurementApiClient.post(
    `/vendor-ui/request-for-proposals/${rfpId}/recipients/${recipientId}/will-not-submit-proposal?reason=${reason}`
  )

export const delegateRecipient = (rfpId: string, uniqueToken: string, email: string): Promise<void> =>
  procurementApiClient.post(`/vendor-ui/request-for-proposals/${rfpId}/recipients/${uniqueToken}/delegate`, { email })

export const postFeedback = (token: string, payload: PostFeedbackType): Promise<void> =>
  procurementApiClient.post(`/vendor-ui/request-for-proposals/proposals/recipients/${token}/feedback`, payload)

export const downloadRfpPdf = (rfpId: string): Promise<AxiosResponse<string>> =>
  procurementApiClient.get(`/vendor-ui/request-for-proposals/${rfpId}/pdf`, { responseType: 'blob' })

export const downloadRfpPdfPublic = (uniqueToken: string): Promise<AxiosResponse<string>> =>
  procurementApiClient.get(`/public/request-for-proposals/${uniqueToken}/pdf`, { responseType: 'blob' })

export const getPublicRfpById = (uniqueToken: string): Promise<RequestForProposalType> =>
  procurementApiClient.get(`/public/request-for-proposals/${uniqueToken}/preview`).then((res) => res.data)

export const postPublicRfpParticipate = (uniqueToken: string, payload: PublicRequestForProposalParticipateType): Promise<void> =>
  procurementApiClient.post(`/public/request-for-proposals/${uniqueToken}/recipients/public-link`, payload)

export const searchCompanies = (searchTerm: string, region: string): Promise<SearchCompaniesResponseType[]> =>
  procurementApiClient
    .get(`/companies/search`, {
      params: {
        searchTerm,
        region
      }
    })
    .then((res) => res.data)
